import React, { useState } from "react";
import { GeneratedEmailResponse, ReviseEmailResponse, WizardAnswers } from "../../types";
import Header from "../components/Header";
import BigScoreCard from "../components/BigScoreCard";
import ReviseBox from "../components/ReviseBox";
import { apiFetch } from "../../office/apiClient";
import { copyText } from "../../office/clipboard";
import styles from "./WriteEmailResultsView.module.css";

interface WriteEmailResultsViewProps {
  result: GeneratedEmailResponse;
  onBack: () => void;
  onClose: () => void;
  // Insert the generated email straight into the active Outlook compose draft.
  onApplyToDraft?: (text: string) => Promise<boolean>;
  // True when an Outlook compose draft is available to write into.
  canApplyToDraft?: boolean;
  // Context for the "Rewrite with my changes" revise call.
  senderName?: string;
  recipientName?: string;
  threadContext?: string;
  wizard?: WizardAnswers | null;
}

export default function WriteEmailResultsView({
  result,
  onBack,
  onApplyToDraft,
  canApplyToDraft = false,
  senderName,
  recipientName,
  threadContext,
  wizard,
}: WriteEmailResultsViewProps) {
  const [feedback, setFeedback] = useState<"up" | "down" | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [applyState, setApplyState] = useState<"idle" | "applied" | "error">("idle");
  // "Rewrite with my changes": revise the generated email in place (no re-score).
  const [revisedBody, setRevisedBody] = useState<string | null>(null);
  const [revisedSubject, setRevisedSubject] = useState<string | null>(null);
  const [showReviseBox, setShowReviseBox] = useState(false);
  const [revising, setRevising] = useState(false);
  const [reviseError, setReviseError] = useState<string | null>(null);

  const currentBody = revisedBody ?? result.body;
  const currentSubject = revisedSubject ?? result.suggestedSubject ?? null;

  const handleCopy = async () => {
    const ok = await copyText(currentBody);
    setCopyState(ok ? "copied" : "error");
    setTimeout(() => setCopyState("idle"), ok ? 1800 : 3000);
  };

  const handleApply = async () => {
    if (!onApplyToDraft) return;
    const ok = await onApplyToDraft(currentBody);
    setApplyState(ok ? "applied" : "error");
    setTimeout(() => setApplyState("idle"), 2200);
  };

  const handleRevise = async (instructions: string) => {
    setRevising(true);
    setReviseError(null);
    try {
      const res = await apiFetch("/api/revise-email/", {
        method: "POST",
        body: JSON.stringify({
          originalBody: currentBody,
          instructions,
          subject: currentSubject ?? undefined,
          threadContext,
          senderName,
          recipientName,
          wizard: wizard ?? undefined,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as ReviseEmailResponse;
      setRevisedBody(data.body);
      if (data.suggestedSubject) setRevisedSubject(data.suggestedSubject);
      setShowReviseBox(false);
    } catch (err: unknown) {
      setReviseError(err instanceof Error ? err.message : "Could not apply your changes. Try again.");
    } finally {
      setRevising(false);
    }
  };

  const canInsert = canApplyToDraft && !!onApplyToDraft;

  return (
    <div className={styles.panel}>
      <Header onBack={onBack} />
      <div className={styles.body}>
        {/* Score card — same look as the analyse screen (Trusted Partner score). */}
        <BigScoreCard score={result.score} verdict={result.verdict} issuesFound={0} />

        {/* Suggested subject */}
        {currentSubject && (
          <div className={styles.subjectRow}>
            <span className={styles.subjectRowLabel}>Suggested subject:</span>
            <span className={styles.subjectRowText}>{currentSubject}</span>
          </div>
        )}

        {/* AI Generated label */}
        <div className={styles.generatedLabel}>AI Generated</div>

        {/* Email body */}
        <div className={styles.emailBody}>{currentBody}</div>

        {/* Caption */}
        <p className={styles.caption}>{result.caption}</p>

        {/* Action buttons */}
        <div className={styles.actionRow}>
          {canInsert && (
            <button
              className={`${styles.actionBtn} ${styles.actionPrimary}`}
              onClick={handleApply}
              type="button"
              title="Insert this email into the current Outlook draft"
            >
              {applyState === "applied"
                ? "✓ Inserted into Draft"
                : applyState === "error"
                  ? "⚠ Could not insert"
                  : "↧ Insert into Draft"}
            </button>
          )}
          <button
            className={`${styles.actionBtn} ${canInsert ? styles.actionSecondary : styles.actionPrimary}`}
            onClick={handleCopy}
            type="button"
          >
            {copyState === "copied"
              ? "✓ Copied!"
              : copyState === "error"
                ? "⚠ Couldn't copy"
                : "✓ Copy to Clipboard"}
          </button>
          <button
            className={`${styles.actionBtn} ${styles.actionSecondary}`}
            onClick={() => {
              setReviseError(null);
              setShowReviseBox((v) => !v);
            }}
            type="button"
          >
            ↻ Rewrite with My Changes
          </button>
        </div>

        {showReviseBox && (
          <ReviseBox
            busy={revising}
            error={reviseError}
            onApply={handleRevise}
            onCancel={() => {
              setShowReviseBox(false);
              setReviseError(null);
            }}
          />
        )}

        {/* Feedback section */}
        <div className={styles.feedbackRow}>
          <span className={styles.feedbackLabel}>Was this draft useful?</span>
          <div className={styles.feedbackBtns}>
            <button
              className={`${styles.thumbBtn} ${feedback === "up" ? styles.thumbActive : ""}`}
              onClick={() => setFeedback("up")}
              aria-label="Yes"
              type="button"
            >
              👍
            </button>
            <button
              className={`${styles.thumbBtn} ${feedback === "down" ? styles.thumbActive : ""}`}
              onClick={() => setFeedback("down")}
              aria-label="No"
              type="button"
            >
              👎
            </button>
          </div>
        </div>

        {/* Back link */}
        <button className={styles.backLink} onClick={onBack} type="button">
          ← Back to home
        </button>
      </div>
    </div>
  );
}
